import { Form, Formik, type FormikHelpers } from 'formik';
import { useUpdateHouseMutation } from 'gcs-common/clients/api/entities/houses/houses-api';
import { getSelectedHouseDetails } from 'gcs-common/slices/administration/houses/housesSelectors';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import CheckIcon from '../../../../img/check_green.svg?react';
import CancelIcon from '../../../../img/close_red.svg?react';
import EditIcon from '../../../../img/edit.svg?react';
import FormikSelect from '../../../FormikSelect/FormikSelect';
import styles from './styles.module.scss';

const readReceiptAllowanceOptions = [
  { value: 'AGENTS_ONLY', label: 'Nur Mitarbeiter sehen Lesebestätigungen' },
  { value: 'ALL', label: 'Mitarbeiter und Kunden sehen Lesebestätigungen' },
  { value: 'NONE', label: 'Niemand sieht Lesebestätigungen' },
];

const validationSchema = Yup.object().shape({
  readReceiptAllowance: Yup.string().nullable().required('Erforderlich'),
});

const HouseReadConfirmation = () => {
  const [isEditing, setIsEditing] = useState(false);
  const selectedHouse = useSelector(getSelectedHouseDetails);

  const updateHouseMutation = useUpdateHouseMutation();

  const startEditing = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditing(true);
  }, []);

  const { readReceiptAllowance } = selectedHouse || {};

  const initialValues = useMemo(() => {
    return {
      readReceiptAllowance: readReceiptAllowance || '',
    };
  }, [readReceiptAllowance]);

  const cancelEditing = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditing(false);
  }, []);

  const submit = useCallback(
    async (values: typeof initialValues, { setSubmitting }: FormikHelpers<typeof initialValues>) => {
      try {
        await updateHouseMutation.mutateAsync(
          {
            path: {
              house_id: selectedHouse.id,
            },
            body: values,
          },
          {
            onSuccess: () => {
              setIsEditing(false);
            },
            onSettled: () => {
              setSubmitting(false);
            },
          },
        );
      } catch {
        // Error toast is handled by the mutation onError callback.
      }
    },
    [selectedHouse, updateHouseMutation],
  );

  return (
    <div className={styles.row}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={submit}
        validateOnChange
      >
        {({ isSubmitting, resetForm }) => (
          <Form noValidate>
            <div className={styles.bottomHeader}>
              <div className={styles.buttonsWrapper}>
                {isEditing ? (
                  <>
                    <button className={styles.saveButton} type="submit" disabled={isSubmitting}>
                      <CheckIcon />
                    </button>
                    <button
                      className={styles.saveButton}
                      type="button"
                      onClick={(event) => {
                        resetForm();
                        cancelEditing(event);
                      }}
                      disabled={isSubmitting}
                    >
                      <CancelIcon />
                    </button>
                  </>
                ) : (
                  <button className={styles.saveButton} type="button" onClick={startEditing}>
                    <EditIcon />
                  </button>
                )}
              </div>
            </div>
            <FormikSelect
              label="Lesebestätigungen in Chats mit Kunden"
              name="readReceiptAllowance"
              placeholder="Bitte wählen"
              options={readReceiptAllowanceOptions}
              disabled={!isEditing}
              labelKey="label"
              valueKey="value"
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default HouseReadConfirmation;
